/**
 * Service Links — tab switching and centring the active item.
 *
 * render.php already emits every children row with the right one visible, so
 * the page a visitor landed on is correct without this script. All it adds is
 * switching rows on click and bringing the active item into view.
 */

import domReady from '@wordpress/dom-ready';

const BLOCK_SELECTOR = '.wp-block-improntad-theme-blocks-service-links';
// Leftover space is split across the two sides of the item to centre it.
const SIDES = 2;

function rowsOf(block) {
  return Array.from(block.querySelectorAll('[data-service-children]'));
}

// scrollIntoView() would scroll every ancestor including the document, which on
// load yanks the whole page toward the rail. Writing scrollLeft moves only the
// row. Measured off bounding rects because offsetLeft is relative to the nearest
// positioned ancestor, which is not the scroll container.
function centreInRow(row, node) {
  if (!row || !node) {
    return;
  }

  const rowBox = row.getBoundingClientRect();
  const nodeBox = node.getBoundingClientRect();
  const delta =
    nodeBox.left - rowBox.left - (row.clientWidth - nodeBox.width) / SIDES;

  row.scrollLeft = Math.max(0, row.scrollLeft + delta);
}

function centreActive(block) {
  const rootsRow = block.querySelector('.service-links-roots');
  const visibleChildren = rowsOf(block).find((row) => !row.hidden);

  centreInRow(rootsRow, block.querySelector('.root-service.active'));
  centreInRow(visibleChildren, visibleChildren?.querySelector('.child-service.active'));
}

function openRoot(block, rootId) {
  block.querySelectorAll('.root-service').forEach((node) => {
    const isOpen = node.dataset.serviceRoot === rootId;

    node.classList.toggle('active', isOpen);

    if (node.tagName === 'BUTTON') {
      node.setAttribute('aria-expanded', String(isOpen));
    }
  });

  rowsOf(block).forEach((row) => {
    row.hidden = row.dataset.serviceChildren !== rootId;
  });
}

function setUp(block) {
  block.addEventListener('click', (event) => {
    // Only the tab triggers are buttons; a childless root is a real link and is
    // left alone to navigate.
    const trigger = event.target.closest('button.root-service');

    if (!trigger || !block.contains(trigger)) {
      return;
    }

    openRoot(block, trigger.dataset.serviceRoot);
  });

  centreActive(block);
}

domReady(() => {
  const blocks = Array.from(document.querySelectorAll(BLOCK_SELECTOR));

  blocks.forEach(setUp);

  // Item widths depend on the rendered text, so centring measured against the
  // fallback face centres the wrong thing once the webfont swaps in. Nothing is
  // re-centred on resize, deliberately: doing so would pull the rail out from
  // under someone who is scrolling it.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => blocks.forEach(centreActive));
  }
});
